import { all, takeEvery, put, call } from 'redux-saga/effects';
import { IRequestParams, request } from 'helpers/request';
import { logSagaErrorMessage } from 'utils';
import {
	getAdminEndpoint,
	getEndpointWithId,
	getEndpointWithParams,
} from 'helpers';
import * as types from '../ActionTypes';
import {
	getClientInvitationTemplate,
	getClientReminderTemplate,
	getEmailTemplatesData,
	postEmailTemplate,
	replaceEmailTemplate,
} from '../settings.actions';
import {
	EmailLetterTypes,
	IPostTemplateParams,
	IReplaceTemplateParams,
} from '../models/email-templates';

const clientDataPath = '/clients/:replaceClientId/email-template-params';
const baseTemplatePath = '/survey/:replaceSurveyId/email-template';
const replaceTemplatePath =
	'/survey/:replaceParam0/email-template/:replaceParam1';

const ENDPOINTS = {
	clientData: getEndpointWithId(getAdminEndpoint(clientDataPath)),
	defaultEmailTemplates: getEndpointWithId(
		getAdminEndpoint(`${baseTemplatePath}/defaults`),
	),
	invitationTemplate: getEndpointWithId(
		getAdminEndpoint(`${baseTemplatePath}/invitation`),
	),
	reminderTemplate: getEndpointWithId(
		getAdminEndpoint(`${baseTemplatePath}/reminder`),
	),
	replaceTemplate: getEndpointWithParams(getAdminEndpoint(replaceTemplatePath)),
};

function* getInvitationTemplateSaga({
	payload: { surveyId },
}: {
	payload: {
		surveyId: string;
	};
}) {
	try {
		yield put(getClientInvitationTemplate.loading());

		const getInvitationTemplateParams: IRequestParams = {
			method: 'GET',
			url: ENDPOINTS.invitationTemplate(surveyId),
		};

		const { data } = yield call(request, getInvitationTemplateParams);

		yield put(
			getClientInvitationTemplate.success({
				clientInvitationTemplate: data,
			}),
		);
	} catch (e) {
		if (e.status === 'NOT_FOUND') {
			yield put(
				getClientInvitationTemplate.success({
					clientInvitationTemplate: null,
				}),
			);
		} else {
			logSagaErrorMessage('get client INVITATION template', e);
			yield put(getClientInvitationTemplate.fail(e));
		}
	}
}

function* getReminderTemplateSaga({
	payload: { surveyId },
}: {
	payload: {
		surveyId: string;
	};
}) {
	try {
		yield put(getClientReminderTemplate.loading());

		const getReminderTemplateParams: IRequestParams = {
			method: 'GET',
			url: ENDPOINTS.reminderTemplate(surveyId),
		};

		const { data } = yield call(request, getReminderTemplateParams);

		yield put(
			getClientReminderTemplate.success({
				clientReminderTemplate: data,
			}),
		);
	} catch (e) {
		if (e.status === 'NOT_FOUND') {
			yield put(
				getClientReminderTemplate.success({
					clientReminderTemplate: null,
				}),
			);
		} else {
			logSagaErrorMessage('get client REMINDER template', e);
			yield put(getClientReminderTemplate.fail(e));
		}
	}
}

function* getEmailTemplatesDataSaga({
	payload: { surveyId, clientId },
}: {
	payload: {
		surveyId: string;
		clientId: string;
	};
}) {
	try {
		yield put(getEmailTemplatesData.loading());

		const getEmailTemplatesParams: IRequestParams = {
			method: 'GET',
			url: ENDPOINTS.defaultEmailTemplates(surveyId),
		};

		const getClientDataParams: IRequestParams = {
			method: 'GET',
			url: ENDPOINTS.clientData(clientId),
		};

		const { data: templateData } = yield call(request, getEmailTemplatesParams);
		const { data: clientData } = yield call(request, getClientDataParams);
		yield call(getInvitationTemplateSaga, { payload: { surveyId } });
		yield call(getReminderTemplateSaga, { payload: { surveyId } });

		yield put(
			getEmailTemplatesData.success({
				templateData,
				clientData,
			}),
		);
	} catch (e) {
		logSagaErrorMessage('get email templates data', e);
		yield put(getEmailTemplatesData.fail(e));
	}
}

function* postEmailTemplateSaga({
	payload: { surveyId, templateData, emailType },
}: {
	payload: IPostTemplateParams;
}) {
	try {
		yield put(postEmailTemplate.loading());

		const url =
			emailType === EmailLetterTypes.INVITATION
				? ENDPOINTS.invitationTemplate(surveyId)
				: ENDPOINTS.reminderTemplate(surveyId);

		const postEmailTemplateParams: IRequestParams = {
			method: 'POST',
			url,
			data: templateData,
		};

		const { data } = yield call(request, postEmailTemplateParams);

		yield put(postEmailTemplate.success(data));
	} catch (e) {
		logSagaErrorMessage('post email template', e);
		yield put(postEmailTemplate.fail(e));
	}
}

function* replaceEmailTemplateSaga({
	payload: { surveyId, templateData, templateId },
}: {
	payload: IReplaceTemplateParams;
}) {
	try {
		yield put(replaceEmailTemplate.loading());

		const postEmailTemplateParams: IRequestParams = {
			method: 'PUT',
			url: ENDPOINTS.replaceTemplate(surveyId, templateId),
			data: templateData,
		};

		const { data } = yield call(request, postEmailTemplateParams);

		yield put(replaceEmailTemplate.success(data));
	} catch (e) {
		logSagaErrorMessage('replace email template', e);
		yield put(replaceEmailTemplate.fail(e));
	}
}

export function* emailEditingSaga() {
	yield all([
		takeEvery(
			// @ts-ignore
			types.GET_EMAIL_TEMPLATES_DATA.DEFAULT,
			getEmailTemplatesDataSaga,
		),
		takeEvery(
			// @ts-ignore
			types.GET_CLIENT_INVITATION_TEMPLATE.DEFAULT,
			getInvitationTemplateSaga,
		),
		takeEvery(
			// @ts-ignore
			types.GET_CLIENT_REMINDER_TEMPLATE.DEFAULT,
			getReminderTemplateSaga,
		),
		// @ts-ignore
		takeEvery(types.POST_EMAIL_TEMPLATE.DEFAULT, postEmailTemplateSaga),
		// @ts-ignore
		takeEvery(types.REPLACE_EMAIL_TEMPLATE.DEFAULT, replaceEmailTemplateSaga),
	]);
}
